<template>
  <transition
    v-if="show"
    enter-class="transform translate-x-cart-sidebar sm:translate-x-full"
    leave-to-class="transform translate-x-cart-sidebar sm:translate-x-full"
    name="slide"
  >
    <div
      class="cart-sidebar"
      id="cart-sidebar"
    >
      <section class="bg-light-yellow pb-2 pt-6 px-6 sm:pt-4 sm:px-4">
        <div class="flex items-center justify-between mb-4 sm:mb-2">
          <h1 class="flex-grow flex-shrink-0 sm:text-lg text-primary-500 text-xl truncate">
            <span v-if="sent">您已完成訂購</span>
            <span v-else>
              您的選購明細<span v-if="isCartEmpty">是空的</span>
            </span>
          </h1>
          <w-button class="h-8 w-8 flex-grow-0 sm:py-0" variant="icon" @click="close">
            <i class="icon-cross" />
          </w-button>
        </div>
        <div v-if="!isCartEmpty" class="min-w-max">
          <div class="mb-3">
            <label class="mr-6 text-gray-500 text-sm">
              訂購名稱
            </label>
            <span :title="infos.name" class="text-black text-sm">
              {{ infos.name }}
            </span>
          </div>
          <div :class="[ hasOnlyOnePeriod ? 'flex-row items-center' : 'flex-col' ]" class="flex">
            <label :class="{ 'mr-7': hasOnlyOnePeriod }"  class="text-gray-500 text-sm">
              領取時段
            </label>
            <span v-if="hasOnlyOnePeriod" class="text-black">
              <displayed-time-format
                :period="firstPeriod"
                text-size-class="sm:text-sm text-base"
              />
            </span>
            <t-select
              v-if="hasMultipleOnePeriod"
              v-model="receivePeriodIndex"
              :options="allowedReceiveOptions"
              class="my-2"
              text-attribute="text"
              value-attribute="value"
              wrapped
            >
              <template #arrow>
                <i class="icon-chevron-down text-gray-500 text-xl" />
              </template>
            </t-select>
          </div>
        </div>
      </section>
      <section
        :class="{ 'max-h-1/2': !sent }"
        class="cart-sidebar__content min-h-1/5 overflow-y-auto overscroll-y-contain px-6 sm:px-4"
      >
        <div v-if="sent" class="flex flex-col items-center p-6">
          <p class="mb-10 sm:whitespace-pre-line truncate">訂單已送出，敬請期待</p>
          <img
            alt="Order Sent"
            class="flex-grow-0 flex-shrink-0"
            src="@/assets/images/order-sent.jpg"
          />
        </div>
        <div v-else-if="isCartEmpty" class="flex flex-col items-center p-6">
          <i class="icon-bag mb-4 text-4xl" />
          <p class="sm:whitespace-pre-line truncate">還沒有任何物品，立即開始訂購喜愛的產品吧</p>
        </div>
        <div v-else>
          <div
            v-for="(item, itemIndex) in items"
            :key="`cart-item-${itemIndex}`"
            class="cart-sidebar__items"
          >
            <div class="flex-grow-0 flex-shrink-0 mr-3 relative">
              <select
                :value="item.count"
                class="cart-sidebar__count-select"
                @input="changeCount($event, itemIndex)"
              >
                <option
                  v-for="(option, optionIndex) in getCartItemCountOptions(item.index, item.count)"
                  :key="`option-${optionIndex}`"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
              <span
                class="absolute flex inset-y-0 items-center pointer-events-none px-1 right-0"
              >
                <i class="icon-chevron-down text-gray-500" />
              </span>
            </div>
            <div class="flex-grow my-4 max-w-[calc(100%-theme(space.16))]">
              <section class="flex items-center justify-between text-black">
                <label :title="item.name" class="flex-grow truncate">
                  {{ item.name }}
                </label>
                <span class="flex-shrink-0 ml-2">
                  {{ item.total | commaFormat | priceFormat }}
                </span>
              </section>
              <section v-if="item.selected && item.selected.length">
                <p
                  v-for="(selectedItem, selectedItemIndex) in item.selected"
                  :key="`selected-item-${selectedItemIndex}`"
                  class="text-gray-500 text-sm"
                >
                  {{ selectedItem.join('、') }}
                </p>
              </section>
              <p v-if="item.note" :title="item.note" class="text-gray-500 text-sm">
                {{ item.note }}
              </p>
              <div
                v-if="isOverEntreeAvailable(item.index)"
                class="
                  border-t
                  border-red-500
                  mt-1
                  text-xs
                  text-red-500
                "
              >
                剩餘可訂購數： {{ getEntreeAvailableCount(item.index) }}
              </div>
            </div>
          </div>
          <div class="cart-sidebar__total">
            <label class="text-black">總計</label>
            <span class="text-secondary-500">
              {{ cartTotal | commaFormat | priceFormat }}
            </span>
          </div>
        </div>
      </section>
      <section v-if="!sent" class="cart-sidebar__footer min-h-1/3 overflow-y-auto py-2">
        <div
          v-if="hasShareKey"
          class="bg-light-gray-400 flex flex-col px-6 py-2 rounded-r-lg rounded-t-lg sm:px-4"
        >
          <label class="text-black">
            訪客姓名
            <span class="text-red-500">(必填)</span>
          </label>
          <w-input
            v-model.trim.lazy="purchaser"
            :message="purchaserMsg"
            :show-error="!purchaser && touched && dirty"
            :show-success="!!(purchaser && touched && dirty)"
            class="mt-2 w-full"
            placeholder="請輸入您的姓名"
            @blur="onTouched"
            @focus="onTouched"
          />
        </div>
        <div
          v-if="hasOverLimit"
          class="cart-sidebar__over-limit"
        >
          <span :title="limitText">
            {{ limitText }}
          </span>
        </div>
        <div class="px-6 sm:px-4">
          <w-button
            :disabled="isSendOrderBtnDisabled"
            :loading="sending"
            class="flex items-center justify-between my-2 w-full"
            @click.prevent="send"
          >
            <span class="border border-white flex-shrink-0 rounded-sm px-2">
              {{ cartItemsCount }}
            </span>
            <section class="flex-shrink-0">
              <span v-if="isSendOrderBtnDisabled">無法</span>
              <span>送單</span>
            </section>
            <span class="flex-shrink-0">
              {{ cartTotal | commaFormat | priceFormat }}
            </span>
          </w-button>
        </div>
      </section>
      <section v-else class="flex items-center justify-center mt-10">
        <w-button @click.prevent="gotoMenu" >回到首頁</w-button>
      </section>
    </div>
  </transition>
</template>

<script>
import CartSidebar from './CartSidebar';

export default CartSidebar;
</script>

<style lang="scss" scoped>
@import 'CartSidebar';
</style>
